import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import NetInfo, { useNetInfo } from '@react-native-community/netinfo';

import OfflineQueueManager from './OfflineQueueManager';
import { OfflineQueueContext } from './OfflineContext';

const DEFAULT_OPERATION_CONFIGS = {};

const getConnectivity = (netInfo) => {
	if (typeof netInfo?.isConnected !== 'boolean') {
		return true;
	}

	if (!netInfo.isConnected) {
		return false;
	}

	if (typeof netInfo.isInternetReachable === 'boolean') {
		return netInfo.isInternetReachable;
	}

	return true;
};

const OfflineProvider = ({
	client,
	operationConfigs = DEFAULT_OPERATION_CONFIGS,
	children,
}) => {
	const netInfo = useNetInfo();
	const managerRef = useRef();
	const configsRef = useRef(operationConfigs);

	if (!managerRef.current) {
		managerRef.current = new OfflineQueueManager({});
	}

	const manager = managerRef.current;
	const [queue, setQueue] = useState(manager.getQueue());
	const [isReady, setIsReady] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [isFlushing, setIsFlushing] = useState(false);
	const readyResolversRef = useRef([]);

	const flush = useCallback(async () => {
		if (manager.isFlushing) {
			return;
		}
		setIsFlushing(true);
		try {
			return await manager.flush(client, configsRef.current);
		}
		finally {
			setIsFlushing(false);
		}
	}, [manager, client]);

	useEffect(() => {
		configsRef.current = operationConfigs;

		if (manager.isOnline && isReady && client) {
			flush();
		}
	}, [operationConfigs, manager, isReady, client, flush]);

	useEffect(() => {
		let unsubscribe = () => {};

		(async () => {
			await manager.initialize();
			setQueue([...manager.getQueue()]);
			setIsReady(true);
			readyResolversRef.current.forEach(resolve => resolve(true));
			readyResolversRef.current = [];
			unsubscribe = manager.subscribe(currentQueue => {
				setQueue([...currentQueue]);
			});
		})();

		return () => {
			unsubscribe();
		};
	}, [manager]);

	useEffect(() => {
		if (!isReady) {
			return;
		}

		const connected = getConnectivity({
			isConnected: netInfo.isConnected,
			isInternetReachable: netInfo.isInternetReachable,
		});
		setIsOnline(connected);
		manager.setOnline(connected);

		if (connected && client) {
			flush();
		}
	}, [netInfo.isConnected, netInfo.isInternetReachable, manager, client, isReady, flush]);

	useEffect(() => {
		if (!isReady || !client || !queue.length) {
			return undefined;
		}

		const retry = () => {
			if (manager.isOnline) {
				flush();
			}
		};
		// An upload can fail while NetInfo stays online, so connectivity events alone are insufficient.
		const interval = setInterval(retry, 30000);
		const subscription = AppState.addEventListener('change', (state) => {
			if (state === 'active') {
				NetInfo.refresh();
				retry();
			}
		});

		return () => {
			clearInterval(interval);
			subscription.remove();
		};
	}, [client, flush, isReady, manager, queue.length]);

	const enqueue = useCallback(async (entry) => {
		const queuedEntry = await manager.enqueue(entry);
		if (manager.isOnline) {
			flush();
		}
		return queuedEntry;
	}, [flush, manager]);

	const remove = useCallback((entryId) => {
		return manager.remove(entryId);
	}, [manager]);

	const resetFailures = useCallback(() => {
		return manager.resetFailures();
	}, [manager]);

	const waitUntilReady = useCallback(() => {
		if (managerRef.current?.isInitialized || isReady) {
			return Promise.resolve(true);
		}

		return new Promise(resolve => {
			readyResolversRef.current.push(resolve);
		});
	}, [isReady]);

	const contextValue = useMemo(() => ({
		isOnline,
		isReady,
		isFlushing,
		queue,
		enqueue,
		remove,
		resetFailures,
		flush,
		waitUntilReady,
	}), [isOnline, isReady, isFlushing, queue, enqueue, remove, resetFailures, flush, waitUntilReady]);

	return (
		<OfflineQueueContext.Provider value={contextValue}>
			{children}
		</OfflineQueueContext.Provider>
	);
};

export default OfflineProvider;
